import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { getAllSlotsByAvailabilityId } from 'services/interventionAvailabilityServices';
import styled from 'styled-components';
import { InterventionSlotStatus } from 'ts-types/api.enums';
import { InterventionAvailabilityDto, InterventionSlotDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const StyledSlots = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin: 10px;
  overflow-y: auto;

  .available-slots-row {
    margin: 0.25rem 0 1rem;
    grid-column-start: 2;
    justify-self: start;

    .time-slot {
      display: inline-block;
      margin-right: 0.357rem;
      padding: 2px 4px;
      border-radius: 4px;
      cursor: default;
      border: 1px solid #e5e5e5;
      margin-bottom: 0.5rem;
    }

    .time-slot.BOOKED {
      background: #f1c6dc;
      border: 1px solid #d8a1bd;
      cursor: default;
    }

    .time-slot:not(.BOOKED):HOVER {
      background: #c4dcef;
    }
  }
`

interface Props extends AuthConsumerRenderProps, MessageSourceProps {
  interventionAvailabilityId: number;
  onCancel: () => void;
  availability: InterventionAvailabilityDto;
}

interface State {
  errorMessage?: string;
  slots: InterventionSlotDto[];

  cancelTokenSource: CancelTokenSource;
}

class InterventionSlotView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const interventionAvailabilityId = props.interventionAvailabilityId;

    this.state = {
      slots: [],
      cancelTokenSource
    }

    getAllSlotsByAvailabilityId(interventionAvailabilityId, cancelTokenSource)
        .then(response => {
          this.setState({
            slots: response
          });
        }).catch(noop)
        .finally();
  };

  handleError = (error: any) => {
    const {getMessage} = this.props;
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    const {availability, getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    const dateDesc = moment(availability.date).format("DD.MM.YYYY");
    const fromTimeDesc = moment(availability.fromTime, "HH:mm:ss").format("HH:mm");
    const toTimeDesc = moment(availability.toTime, "HH:mm:ss").format("HH:mm");

    const labelDesc = `${dateDesc}, ${fromTimeDesc} - ${toTimeDesc}`

    return (
        <form>
          <FormContent>
            {errorMessage
            && (
                <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                  {errorMessage}
                </StyledErrorMessage>
            )
            }
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column width={6} textAlign={'left'}>
                  <strong>{labelDesc}</strong>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={16}>
                  {this.renderAvailableSlots()}
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
          </FormContent>

          <FormButtonsRow>
            <Button compact type="button" onClick={onCancel}>{getMessage("button.cancel")}
            </Button>
          </FormButtonsRow>
        </form>
    );
  };

  renderAvailableSlots = (): JSX.Element => {
    const {slots} = this.state;

    let currentHour = 0;
    let currentMin = 0;

    const displayedSlots = slots.filter(slot => InterventionSlotStatus.BLOCKED !== slot.status);

    const slotNodes = displayedSlots.map((slotDto, ix) => {
          let newRow = false;
          const slot = moment(slotDto.fromTime, "HH:mm")

          const slotHour = slot.hour();
          if (slotHour !== currentHour) {
            newRow = true;
            currentHour = slotHour;
          }

          const slotMin = slot.minute();
          if (currentMin !== Math.floor(slotMin / 15)) {
            newRow = true;
            currentMin = Math.floor(slotMin / 15);
          }

          const bookedClassname = slotDto.status ? slotDto.status : "";

          return <Fragment key={ix}>
            {newRow && <div className="new-row-div"/>}
            {ix > 0 && !newRow && <> </>}
            <div
                className={`time-slot ${bookedClassname}`}
            >
              {slot.format("HH:mm")}
            </div>
          </Fragment>
        }
    );

    return <StyledSlots>
      <div className={"available-slots-row"}>{slotNodes}</div>
    </StyledSlots>;
  };

}

export default withAuthContext(withMessages(InterventionSlotView));
